import { cloneDeep, set } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { getAssetTree } from '../common/apis'
import { ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { analysisEditingUrl, getSkuSelectOptionsFromAssetTree } from './analysisUtils'
import * as yup from 'yup'
import i18n from 'live/services/i18n'
import LiveSelect from 'live/components/forms/ui/select'
import { ANALYSIS_TYPES } from '../common/utils'
import LiveTooltip from 'live/components/ui/tooltip'
import { EmptyFields } from './analysisTypes'
import { Asterisk } from '../common/components'

interface IProps {
    generalInfo: GeneralInfo
    setGeneralInfo: React.Dispatch<React.SetStateAction<GeneralInfo>>
    displayColumn?: boolean
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}

export function ProductInfo({
    generalInfo,
    setGeneralInfo,
    displayColumn,
    emptyFields,
    setEmptyFields
}: IProps): JSX.Element {
    const urlParams = useContext(ParamsContext).split('/')
    const [skuSelectOptions, setSkuSelectOptions] = useState<SkuSelectOptions[]>([])
    const [skuInputState, setSkuInputState] = useState<string>('')
    const [batchInputState, setBatchInputState] = useState<string>('')
    const [referenceInputState, setReferenceInputState] = useState<string>('')

    const batchValidation = yup
        .number()
        .min(0)
        .max(999)
        .integer()

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [paramsTab, paramsCountry, paramsFactory, paramsLine, paramsProduct] = urlParams

    useEffect(() => {
        if (emptyFields.fields.includes('sku')) {
            setSkuInputState('Alert')
        }
        if (emptyFields.fields.includes('batch')) {
            setBatchInputState('Alert')
        }
        if (emptyFields.fields.includes('reference')) {
            setReferenceInputState('Alert')
        }
    }, [emptyFields.fields, emptyFields.trigger])

    function onChangeInput(type: 'sku' | 'batch' | 'reference', value: string): void {
        const localEmptyFields = emptyFields
        localEmptyFields.trigger = false

        if (type === 'batch') {
            localEmptyFields.fields = localEmptyFields.fields.filter(field => {
                return field !== 'batch'
            })
            setEmptyFields(localEmptyFields)
            setBatchInputState('')
            if (!batchValidation.isValidSync(Number(value)) || value.length > 3) {
                return
            }
        }
        const updatedInfo = cloneDeep(generalInfo)
        if (type === 'sku') {
            localEmptyFields.fields = localEmptyFields.fields.filter(field => {
                return field !== 'sku'
            })
            setEmptyFields(localEmptyFields)
            setSkuInputState('')

            const skuProduct = skuSelectOptions.filter(option => option.value === value)[0].product
            set(updatedInfo, 'product', skuProduct)
        }

        set(updatedInfo, type, value)
        setGeneralInfo(updatedInfo)
    }

    useEffect(() => {
        const typesArray = Object.values(ANALYSIS_TYPES) as string[]
        const product =
            typesArray.includes(paramsProduct) || paramsProduct === analysisEditingUrl ? null : paramsProduct
        getAssetTree('factory').then(response => {
            setSkuSelectOptions(
                getSkuSelectOptionsFromAssetTree(
                    response as CountryData[],
                    paramsCountry,
                    paramsFactory,
                    paramsLine,
                    product
                )
            )
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <StyledProductInfo className={`productInfo ${displayColumn ? 'displayColumn' : ''}`}>
            <h1 className="productInfo--title">
                {i18n('product')}{' '}
                <LiveTooltip
                    position="top"
                    content={i18n('Fields referring to the product of the analysis. All fields are mandatory.')}
                    hide={false}
                >
                    <span className="productInfo--inputs__sku--title__info">
                        <i className="far fa-info-circle"></i>
                    </span>
                </LiveTooltip>
            </h1>
            <div className="productInfo--inputs">
                <div className={`productInfo--inputs__sku${skuInputState}`}>
                    <h3 className="productInfo--inputs__sku--title">
                        {i18n('product')} <Asterisk />
                    </h3>
                    <LiveSelect
                        className="productInfo--inputs__skuAlert--input"
                        style={{ border: 'none' }}
                        options={skuSelectOptions}
                        value={generalInfo.sku}
                        onChange={value => onChangeInput('sku', value)}
                        placeholder={i18n(skuSelectOptions.length ? 'Choose an sku' : 'loading skus')}
                    />
                </div>
                <div className={`productInfo--inputs__batch${batchInputState}`}>
                    <h3 className="productInfo--inputs__batch--title">
                        {i18n('batch')} <Asterisk />
                    </h3>
                    <input
                        type="number"
                        value={generalInfo.batch}
                        onChange={event => onChangeInput('batch', event.target.value)}
                        onBlur={event => onChangeInput('batch', event.target.value.padStart(3, '0'))}
                        className={`productInfo--inputs__batch--input${batchInputState}`}
                        placeholder={i18n('type batch number')}
                    />
                </div>
                <div className={`productInfo--inputs__comparisson${referenceInputState}`}>
                    <h3 className="productInfo--inputs__comparisson--title">
                        {i18n('Gold Standard')} <Asterisk />
                    </h3>
                    <LiveSelect
                        className="productInfo--inputs__comparisson--input"
                        style={{ border: 'none' }}
                        options={[
                            { value: 'true', label: i18n('yes') },
                            { value: 'false', label: i18n('no') }
                        ]}
                        value={generalInfo.reference.toString()}
                        onChange={value => onChangeInput('reference', JSON.parse(value))}
                    />
                </div>
            </div>
        </StyledProductInfo>
    )
}

const StyledProductInfo = styled.div`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    border: var(--border);
    border-radius: 5px;
    width: 100%;
    display: flex;
    flex-direction: column;
    .productInfo {
        &--title {
            width: 100%;
            padding: 12px 24px;
            text-transform: uppercase;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-800']};
            border-bottom: var(--border);
            font-size: 16px;
            line-height: 16px;
        }
        &--inputs {
            display: flex;
            flex-direction: row;
            &__required {
                color: #d9534f;
                font: 'Source Sans Pro';
            }
            &__batchAlert,
            &__comparissonAlert,
            &__skuAlert {
                background-color: #fdd0cf;
            }
            &__skuAlert {
                &,
                &:hover,
                .Select-control:hover {
                    box-shadow: none;
                }
                .Select-control {
                    .Select-input {
                        display: inline !important;
                    }
                    &,
                    input {
                        width: 100% !important;
                        background-color: #fdd0cf;
                    }
                }
            }
            &__batchAlert,
            &__comparissonAlert,
            &__skuAlert,
            &__sku,
            &__batch,
            &__comparisson {
                flex-grow: 1;
                max-width: 33%;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                padding: 12px 24px 6px;
                border: 1px solid transparent;
                &--title {
                    text-transform: capitalize;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 15px;
                    line-height: 15px;
                    margin-bottom: 5px;
                    &__info {
                        color: #37a3ef;
                        font-size: 14px;
                    }
                }

                &,
                &:hover,
                .Select-control:hover {
                    box-shadow: none;
                }
                .Select-control {
                    .Select-input {
                        display: inline !important;
                    }
                    &,
                    input {
                        width: 100% !important;
                    }
                }
            }
            &__batchAlert,
            &__skuAlert,
            &__sku,
            &__batch {
                border-right: var(--border);
            }
            &__comparissonAlert,
            &__skuAlert,
            &__sku,
            &__comparisson {
                .live-select {
                    width: 100%;
                    margin: 0;
                }
            }
            &__batch {
                &--input {
                    border: none;
                    box-shadow: none;
                    padding: 0;
                }
                &--inputAlert {
                    border: none;
                    box-shadow: none;
                    padding: 0;
                    background-color: #fdd0cf;
                }
            }
            &__batchAlert,
            &__comparissonAlert,
            &__skuAlert,
            &__sku,
            &__batch,
            &__comparisson {
                &:focus-within {
                    border: 1px solid var(--blue-500);
                }
            }
        }
    }
    &.displayColumn {
        .productInfo {
            &--inputs {
                flex-wrap: wrap;
                &__skuAlert,
                &__sku {
                    width: 100%;
                    border-right: none;
                    border-bottom: var(--border);
                }
                &__batchAlert,
                &__comparissonAlert,
                &__skuAlert,
                &__sku,
                &__batch,
                &__comparisson {
                    max-width: 100%;

                    &:focus-within {
                        border: 1px solid var(--blue-500);
                    }
                }
            }
        }
    }
`
